import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, ClipboardPlus, RotateCcw, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { KIT_CONTENTS, PART_GROUPS, partById } from '../data/ul101Parts';
import { addToQuoteList } from '../lib/quoteList';
import { trackEvent } from '../lib/analytics';

const KEY = 'ul101-kit-check';
const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

export const UL101KitChecklist = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [have, setHave] = useState(read);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(have)); }, [have]);

  const items = useMemo(() => KIT_CONTENTS.map((c) => ({ ...c, part: partById(c.pn) })).filter((c) => c.part), []);
  const groups = useMemo(() => PART_GROUPS.filter((g) => g.id !== 'all' && items.some((i) => i.part.group === g.id)).map((g) => ({ ...g, items: items.filter((i) => i.part.group === g.id) })), [items]);
  const missing = useMemo(() => items.filter((i) => !have[i.pn]), [items, have]);
  const present = items.length - missing.length;

  const toggle = (pn) => setHave((h) => ({ ...h, [pn]: !h[pn] }));
  const reset = () => setHave({});
  const reorder = () => {
    addToQuoteList(Object.fromEntries(missing.map((m) => [m.pn, m.qty])));
    trackEvent('parts_list_quote', { product: 'UL101', resource: `kit-check-${missing.length}` });
    setOpen(false);
    onAdded?.(missing.length);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-[#1e2d3b] bg-white px-4 py-2 text-xs font-semibold text-[#1e2d3b] hover:bg-[#1e2d3b] hover:text-white transition-colors" data-testid="ul101-kit-check-open">
        <ClipboardCheck className="h-4 w-4" /> Check my kit is complete
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 bg-white" data-testid="ul101-kit-check-dialog">
          <div className="px-6 pt-6">
            <DialogTitle className="text-lg font-bold text-[#1e2d3b] pr-8">B00033 / SPL-1473 kit completeness check</DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">Tick each component you have in the case. Anything left unticked is treated as missing and can be added to one quote request.</DialogDescription>
            <p className="mt-3 text-sm font-semibold text-[#1e2d3b]" data-testid="ul101-kit-check-summary">{present} of {items.length} components present · <span className={missing.length ? 'text-orange-600' : 'text-emerald-600'}>{missing.length} missing</span></p>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {groups.map((g) => (
              <fieldset key={g.id}>
                <legend className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">{g.label}</legend>
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                  {g.items.map((i) => {
                    const on = !!have[i.pn];
                    return (
                      <li key={i.pn}>
                        <label className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${on ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`} data-testid={`ul101-kit-check-item-${i.pn}`}>
                          <input type="checkbox" checked={on} onChange={() => toggle(i.pn)} className="sr-only peer" data-testid={`ul101-kit-check-box-${i.pn}`} />
                          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${on ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'}`} aria-hidden="true">{on && <Check className="h-3.5 w-3.5" />}</span>
                          {i.part.image ? <img src={i.part.image} alt="" className="h-8 w-12 object-contain" loading="lazy" /> : <span className="h-8 w-12" />}
                          <span className="flex-1 min-w-0">
                            <span className={`block text-sm ${on ? 'text-slate-500 line-through decoration-slate-300' : 'text-[#1e2d3b] font-semibold'}`}>{i.part.name}</span>
                            <span className="block font-mono text-[11px] text-slate-400">P/N {i.pn}{i.qty > 1 ? ` · qty ${i.qty}` : ''}</span>
                          </span>
                          {!on && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700">Missing</span>}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 border-t border-slate-200 px-6 py-4">
            <button type="button" onClick={reset} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-slate-400 transition-colors" data-testid="ul101-kit-check-reset">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button type="button" onClick={reorder} disabled={!missing.length} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-40 disabled:hover:bg-orange-500 transition-colors" data-testid="ul101-kit-check-reorder">
              <ClipboardPlus className="h-4 w-4" /> {missing.length ? `Add ${missing.length} missing part${missing.length === 1 ? '' : 's'} to quote list` : 'Kit complete — nothing to reorder'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
